<template>
  <section class="hero is-default is-bold">
    <div class="hero-body">
      <div class="container">
        <div class="columns">
          <div class="column is-one-quarter">
            <b-field v-if="authStore.isAuthenticated && routeParam">
              <div class="control is-expanded is-clearfix">
                <b-dropdown :expanded="true" aria-role="list">
                  <template #trigger="{ active }">
                    <b-button
                      expanded
                      :label="buttonLabel"
                      type="is-primary"
                      :icon-left="buttonIcon"
                      :icon-right="active ? 'caret-up' : 'caret-down'"
                    />
                  </template>
                  <b-dropdown-item
                    v-for="ghgi in adminStore.allGHGI"
                    :key="ghgi.id"
                    aria-role="listitem"
                    @click="loadGHGI(ghgi.id)"
                  >
                    <b-icon icon="calendar" /> {{ $t('ghgi') }} {{ ghgi.year }}
                  </b-dropdown-item>
                  <b-dropdown-item aria-role="listitem" @click="createGHGI()">
                    <b-icon icon="plus" /> {{ $t('viewGES1point5.label-33') }}
                  </b-dropdown-item>
                  <b-dropdown-item aria-role="listitem" @click="backToAdmin()">
                    <b-icon icon="chevron-left" />
                    {{ $t('viewGES1point5.label-35') }}
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </b-field>
            <b-menu>
              <b-menu-list :label="$t('viewGES1point5.label-17')">
                <b-menu-item
                  @click="activate('introduction')"
                  :active="activeModule === 'introduction'"
                >
                  <template slot="label">
                    <nav class="level">
                      <div class="level-left">
                        <div class="level-item">
                          <b-icon icon="info"></b-icon>
                          {{ $t('viewGES1point5.label-18') }}
                        </div>
                      </div>
                    </nav>
                  </template>
                </b-menu-item>
                <b-menu-item
                  @click="activate('boundaries')"
                  :active="activeModule === 'boundaries'"
                  :disabled="boundariesIsDisabled"
                >
                  <template slot="label">
                    <nav class="level">
                      <div class="level-left">
                        <div class="level-item">
                          <b-icon icon="square"></b-icon>
                          {{ $t('viewGES1point5.label-19') }}
                        </div>
                      </div>
                      <div class="level-right">
                        <div class="level-item">
                          <b-tag
                            v-show="
                              flags.boundaries.isValid |
                                flags.boundaries.isModified
                            "
                            :type="tagType(flags.boundaries.isModified)"
                            rounded
                          >
                            <b-icon :icon="validSavedIcon" />
                          </b-tag>
                        </div>
                      </div>
                    </nav>
                  </template>
                </b-menu-item>
                <b-menu-item
                  v-for="(module, index) in modules"
                  :key="index"
                  @click="activate(module)"
                  :active="activeModule === module"
                  :disabled="dataIsDisabled"
                >
                  <template slot="label">
                    <nav class="level" :data-testid="'menu-' + module">
                      <div class="level-left">
                        <div class="level-item">
                          <b-icon :icon="getIcon(module)"></b-icon>
                          {{ $t(module) }}
                        </div>
                      </div>
                      <div class="level-right">
                        <div class="level-item">
                          <b-tag
                            rounded
                            :type="tagType(flags[module].isModified)"
                            v-if="flags[module].isValid"
                          >
                            <b-icon :icon="validSavedIcon"></b-icon>
                          </b-tag>
                        </div>
                      </div>
                    </nav>
                  </template>
                </b-menu-item>
              </b-menu-list>
              <b-menu-list :label="$t('viewGES1point5.label-28')">
                <b-menu-item
                  @click="activate('ghg-protocol')"
                  :active="activeModule === 'ghg-protocol'"
                  :disabled="reglementaireIsDisabled"
                >
                  <template slot="label">
                    <nav class="level">
                      <div class="level-left">
                        <div class="level-item">
                          <b-icon icon="dot-circle-o"></b-icon>
                          {{ $t('ghg-protocol') }}
                        </div>
                      </div>
                    </nav>
                  </template>
                </b-menu-item>
                <b-menu-item
                  @click="activate('regulatory-inventory')"
                  :active="activeModule === 'regulatory-inventory'"
                  :disabled="reglementaireIsDisabled"
                >
                  <template slot="label">
                    <nav class="level">
                      <div class="level-left">
                        <div class="level-item">
                          <b-icon icon="bank"></b-icon>
                          {{ $t('viewGES1point5.label-25') }}
                        </div>
                      </div>
                    </nav>
                  </template>
                </b-menu-item>
                <b-menu-item
                  @click="activate('carbon-footprint-synthesis')"
                  :active="activeModule === 'carbon-footprint-synthesis'"
                  :disabled="reglementaireIsDisabled"
                >
                  <template slot="label">
                    <nav class="level">
                      <div class="level-left">
                        <div class="level-item">
                          <b-icon icon="paw"></b-icon>
                          {{ $t('viewGES1point5.label-26') }}
                        </div>
                      </div>
                    </nav>
                  </template>
                </b-menu-item>
              </b-menu-list>
            </b-menu>
          </div>
          <div class="column">
            <!-- Data -->
            <introduction
              v-if="activeModule === 'introduction'"
              :flags="flags.introduction"
              @invalid="invalidateIntroduction"
              @valid="validateIntroduction"
            />
            <boundaries
              v-if="activeModule === 'boundaries'"
              :flags="flags.boundaries"
              @is-valid="(val) => (flags.boundaries.isValid = val)"
              @is-modified="(val) => (flags.boundaries.isModified = val)"
            />
            <component
              v-for="(module, index) in modules"
              :key="index"
              :is="module"
              :ref="module"
              v-show="activeModule === module"
              :flags="flags[module]"
              @is-valid="(val) => updateValid(val, module)"
              @is-modified="(val) => updateModified(val, module)"
            />

            <!-- Results -->
            <ghg-protocol v-if="activeModule === 'ghg-protocol'" />
            <regulatory-inventory
              v-if="activeModule === 'regulatory-inventory'"
            />
            <carbon-footprint-synthesis
              v-if="activeModule === 'carbon-footprint-synthesis'"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import Modules from '@/models/Modules.js'
import Vue from 'vue'
import { useAdminStore } from '@/stores/admin.js'
import { useAuthenticationStore } from '@/stores/authentication.js'
import { useGHGIStore } from '@/stores/ghgi.js'
import { useCoreStore } from '@/stores/core.js'

export default {
  name: 'GES1point5',
  components: {
    introduction: () => import('@/components/carbon/forms/Introduction.vue'),
    boundaries: () =>
      import('@/components/carbon/forms/boundaries/Boundary.vue'),
    'ghg-protocol': () => import('@/components/carbon/results/GHGProtocol.vue'),
    'regulatory-inventory': () =>
      import('@/components/carbon/results/RegulatoryInventory.vue'),
    'carbon-footprint-synthesis': () =>
      import('@/components/carbon/results/CarbonFootprintSynthesis.vue')
  },
  data () {
    return {
      isNewGHGI: true,
      activeModule: 'introduction',
      flags: {
        introduction: {
          isValid: false
        },
        boundaries: {
          isValid: false,
          isModified: false
        }
      }
    }
  },
  setup () {
    const adminStore = useAdminStore()
    const authStore = useAuthenticationStore()
    const ghgiStore = useGHGIStore()
    const coreStore = useCoreStore()
    return { adminStore, authStore, ghgiStore, coreStore }
  },
  computed: {
    modules: function () {
      // backward compatibility
      // return the type of the modules (aka travels, buildings, ...)
      return this.modulesObjs.modules.map((m) => m.type)
    },
    modulesObjs: function () {
      return this.coreStore.activeModulesObjs
    },
    ghgiYear: function () {
      return this.ghgiStore.item.year
    },
    validSavedIcon: function () {
      let icon = null
      if (this.authStore.isAuthenticated) {
        icon = 'save'
      } else {
        icon = 'check'
      }
      return icon
    },
    buttonLabel: function () {
      if (this.ghgiYear) {
        return this.$t('ghgi') + ' ' + this.ghgiYear
      } else {
        return this.$t('viewGES1point5.label-33')
      }
    },
    buttonIcon: function () {
      if (this.ghgiYear) {
        return 'calendar'
      } else {
        return 'plus'
      }
    },
    routeParam: function () {
      return this.$route.params.id
    },
    dataIsDisabled: function () {
      let isDisabled = false
      if (this.flags.introduction.isValid) {
        isDisabled =
          !this.flags.boundaries.isValid && !this.flags.boundaries.isModified
      } else {
        isDisabled = true
      }
      return isDisabled
    },
    boundariesIsDisabled: function () {
      let isDisabled = true
      if (this.authStore.isAuthenticated && !this.isNewGHGI) {
        isDisabled = false
      } else if (
        this.authStore.isAuthenticated &&
        this.flags.introduction.isValid
      ) {
        isDisabled = false
      } else if (
        !this.authStore.isAuthenticated &&
        this.flags.introduction.isValid
      ) {
        isDisabled = false
      }
      return isDisabled
    },
    reglementaireIsDisabled: function () {
      let disabled = true
      for (let module of this.modules) {
        disabled = disabled && !this.flags[module].isValid
      }
      return disabled
    }
  },
  watch: {
    routeParam: function () {
      this.parameterInitState()
    }
  },
  methods: {
    initState: function () {
      for (let module of this.modulesObjs) {
        Vue.component(module.type, module.entryPoint)
        Vue.set(this.flags, module.type, {
          isValid: false,
          isModified: false
        })
      }
    },
    getIcon: function (module) {
      return Modules.getIcon(module)
    },
    updateModified (val, module) {
      this.flags[module].isModified = val
    },
    updateValid (val, module) {
      this.flags[module].isValid = val
    },
    tagType: function (isModified) {
      let tagType = 'is-success'
      if (isModified) {
        tagType = 'is-danger'
      }
      return tagType
    },
    loadGHGI (ghgiID) {
      const loadingComponent = this.$buefy.loading.open({
        container: null
      })
      this.ghgiStore
        .set(ghgiID)
        .then((data) => {
          loadingComponent.close()
          this.$router.push('/ges-1point5/' + ghgiID)
        })
        .catch((erreur) => {
          loadingComponent.close()
        })
    },
    backToAdmin () {
      this.ghgiStore.resetGHGI()
      this.$router.push('/administration/all-ghgi')
    },
    createGHGI () {
      this.ghgiStore.resetGHGI()
      this.$router.push('/ges-1point5/0')
    },
    validateIntroduction: function () {
      this.flags.introduction.isValid = true
      this.activate('boundaries')
    },
    invalidateIntroduction: function () {
      this.flags.introduction.isValid = false
      this.ghgiStore.resetState()
    },
    activate: function (module) {
      this.activeModule = module
      if (module === Modules.PURCHASES) {
        this.$refs[Modules.PURCHASES][0].forceComputation()
      }
    },
    parameterInitState: function () {
      // make sure we have one flag object per module
      if (this.routeParam) {
        if (this.routeParam === '0') {
          this.activate('introduction')
          this.isNewGHGI = true
          // reset modules state
          this.flags.introduction.isValid = false
          this.flags.boundaries.isValid = false
          this.flags.boundaries.isModified = false
          for (let module of this.modules) {
            this.flags[module].isValid = false
            this.flags[module].isModified = false
          }
          this.ghgiStore.init()
        } else {
          this.activate('boundaries')
          this.isNewGHGI = false
          this.flags.introduction.isValid = true
          this.flags.boundaries.isValid = true
          for (let module of this.modules) {
            this.flags[module].isValid = this.ghgiStore.hasItems(module)
          }
        }
      }
    }
  },
  beforeMount () {
    this.initState()
  },
  mounted () {
    this.ghgiStore.init()
    if (this.authStore.isAuthenticated) {
      const loadingComponent = this.$buefy.loading.open({
        container: null
      })
      this.adminStore
        .getEntity()
        .then((data) => {
          if (data) {
            this.adminStore
              .getAllTagCategories()
              .then((data) => {
                this.adminStore
                  .getAllGHGIWithoutConsumptions()
                  .then((data) => {
                    if (this.routeParam) {
                      if (this.routeParam !== '0') {
                        this.ghgiStore
                          .set(parseInt(this.routeParam))
                          .then((data) => {
                            loadingComponent.close()
                            this.parameterInitState()
                          })
                      } else {
                        loadingComponent.close()
                        this.parameterInitState()
                      }
                    } else {
                      loadingComponent.close()
                      this.parameterInitState()
                    }
                  })
                  .catch((erreur) => {
                    loadingComponent.close()
                  })
              })
              .catch((erreur) => {
                loadingComponent.close()
              })
          } else {
            loadingComponent.close()
          }
        })
        .catch((erreur) => {
          loadingComponent.close()
        })
    }
  }
}
</script>

<style>
.menu-list a.is-active {
  background-color: rgba(52, 59, 64, 0.7) !important;
  color: white;
}
</style>
